/** @jsxImportSource react */
import { useMemo, useState } from 'react'
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Paper, Skeleton, Stack, Switch, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dashboardsAPI, formsAPI, recordsAPI } from '../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../hooks/useCrmQueries'
import { selectIsTenantAdmin } from '../store/slices/tenantSlice'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import { allFields } from '../components/records/fieldValues'
import { RANGE_PRESETS, autoTitle, newWidget, widgetId } from '../components/dashboard/dashboardModel'
import WidgetCard from '../components/dashboard/WidgetCard'
import WidgetEditorDialog from '../components/dashboard/WidgetEditorDialog'
import { entityLabelPlural } from '../utils/helpers'
import { getErrorMessage } from '../utils/apiErrors'
import { useNotify } from '../components/common/NotificationProvider'
import EmptyState from '../components/common/EmptyState'
import ConfirmDialog from '../components/common/ConfirmDialog'
import Loading from '../components/common/Loading'
import ConnectCalendarPrompt from '../components/meetings/ConnectCalendarPrompt'

const readRange = (key) => { try { return localStorage.getItem(key) || 'last30' } catch { return 'last30' } }
const storeRange = (key, value) => { try { localStorage.setItem(key, value) } catch { /* convenience only */ } }

/** Home: the tenant's default dashboard (or /dashboards/:id), with editing for admins. */
const DashboardPage = () => {
  const { dashboardId } = useParams()
  const navigate = useNavigate()
  const tenantId = useTenantKey()
  const isAdmin = useSelector(selectIsTenantAdmin)
  const { data: dashboards, isLoading } = useQuery({ queryKey: [tenantId, 'dashboards'], queryFn: dashboardsAPI.list })

  if (isLoading) return <Loading />
  const current = dashboards.find((d) => d.id === dashboardId) || (!dashboardId && dashboards[0])
  return (
    <>
      {/* once, until connected or dismissed: right after someone first signs in */}
      {!dashboardId && <ConnectCalendarPrompt />}
      {!current ? (dashboardId ? <Alert severity="warning">Dashboard not found.</Alert> : <NoDashboard isAdmin={isAdmin} />)
        : <Dashboard key={current.id} dashboard={current} dashboards={dashboards} isAdmin={isAdmin} onSwitch={(id) => navigate(`/dashboards/${id}`)} />}
    </>
  )
}

const Dashboard = ({ dashboard, dashboards, isAdmin, onSwitch }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const navigate = useNavigate()
  const { entityTypes } = useEntityTypes()
  const rangeKey = `crm.dashRange.${tenantId}.${dashboard.id}`
  const [rangeId, setRangeId] = useState(() => readRange(rangeKey))
  const [draft, setDraft] = useState(null) // non-null while editing
  const [editing, setEditing] = useState(null) // widget being edited in the dialog
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [creating, setCreating] = useState(false)

  const view = draft || dashboard
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify({ name: dashboard.name, isDefault: dashboard.isDefault, widgets: dashboard.widgets })
  const unsavedDialog = useUnsavedChangesGuard(dirty, 'This dashboard has unsaved changes. Leave without saving?')

  const save = useMutation({
    mutationFn: () => dashboardsAPI.update(dashboard.id, draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'dashboards'] })
      setDraft(null)
      notify('Dashboard saved')
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const remove = useMutation({
    mutationFn: () => dashboardsAPI.remove(dashboard.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'dashboards'] })
      setDraft(null)
      notify('Dashboard deleted')
      navigate('/', { replace: true })
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  const startEdit = () => setDraft({ name: dashboard.name, isDefault: dashboard.isDefault, widgets: dashboard.widgets })
  const updateWidgets = (fn) => setDraft((d) => ({ ...d, widgets: fn(d.widgets) }))
  const saveWidget = (w) => {
    updateWidgets((ws) => (ws.some((x) => x.id === w.id) ? ws.map((x) => (x.id === w.id ? w : x)) : [...ws, w]))
    setEditing(null)
  }
  const move = (index, delta) => updateWidgets((ws) => {
    const next = [...ws]
    const [item] = next.splice(index, 1)
    next.splice(index + delta, 0, item)
    return next
  })

  return (
    <>
      {/* one row above everything it scopes: dashboard, date range, actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {draft ? (
          <TextField label="Dashboard name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} fullWidth={false} sx={{ minWidth: 240 }} />
        ) : dashboards.length > 1 ? (
          <TextField select value={dashboard.id} onChange={(e) => onSwitch(e.target.value)} fullWidth={false}
            sx={{ minWidth: 220, '& .MuiInputBase-input': { fontSize: '1.4rem', fontWeight: 600, py: 0.5 } }} inputProps={{ 'aria-label': 'Dashboard' }}>
            {dashboards.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}{d.isDefault ? ' · default' : ''}</MenuItem>)}
          </TextField>
        ) : (
          <Typography variant="h4">{dashboard.name}</Typography>
        )}
        <TextField select label="Date range" value={rangeId} onChange={(e) => { setRangeId(e.target.value); storeRange(rangeKey, e.target.value) }}
          fullWidth={false} sx={{ minWidth: 160 }}>
          {RANGE_PRESETS.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        {isAdmin && !draft && (
          <>
            <Button startIcon={<AddIcon />} onClick={() => setCreating(true)}>New dashboard</Button>
            <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={startEdit}>Edit</Button>
          </>
        )}
        {draft && (
          <>
            <FormControlLabel control={<Switch checked={draft.isDefault} onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))} />} label="Show on Home" />
            <Button color="error" onClick={() => setConfirmDelete(true)}>Delete</Button>
            <Button onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending || !draft.name.trim()}>Save</Button>
          </>
        )}
      </Box>

      {view.widgets.length === 0 && !draft && (
        <Paper variant="outlined">
          <EmptyState icon={<InsightsOutlinedIcon />} title="This dashboard is empty"
            description={isAdmin ? 'Edit it to add numbers, charts and top lists.' : 'An admin hasn’t added any widgets yet.'} />
        </Paper>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 2 }}>
        {view.widgets.map((w, i) => (
          <Box key={w.id} sx={{ gridColumn: { xs: 'span 12', md: `span ${Math.min(12, w.width === 4 ? 6 : w.width || 4)}`, lg: `span ${w.width || 4}` } }}>
            <WidgetTitle widget={w}>
              {(title) => (
                <WidgetCard widget={w} title={title} rangeId={rangeId} editing={!!draft}
                  isFirst={i === 0} isLast={i === view.widgets.length - 1}
                  onEdit={() => setEditing(w)} onMove={(d) => move(i, d)}
                  onRemove={() => updateWidgets((ws) => ws.filter((x) => x.id !== w.id))} />
              )}
            </WidgetTitle>
          </Box>
        ))}
        {draft && (
          <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6', lg: 'span 4' } }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEditing(newWidget(entityTypes[0]))}
              sx={{ width: '100%', minHeight: 140, borderStyle: 'dashed' }} disabled={entityTypes.length === 0}>
              Add widget
            </Button>
          </Box>
        )}
      </Box>

      <WidgetEditorDialog open={!!editing} initial={editing} isNew={!!editing && !view.widgets.some((x) => x.id === editing.id)}
        rangeId={rangeId} onSave={saveWidget} onClose={() => setEditing(null)} />
      <NewDashboardDialog open={creating} onClose={() => setCreating(false)} />
      <ConfirmDialog open={confirmDelete} title="Delete dashboard?" message={`“${dashboard.name}” will be removed for everyone. Records are not affected.`}
        confirmLabel="Delete" destructive busy={remove.isPending} onConfirm={() => remove.mutate()} onClose={() => setConfirmDelete(false)} />
      {unsavedDialog}
    </>
  )
}

/** Widgets without a custom title get one generated from their definition (needs the form's labels). */
const WidgetTitle = ({ widget, children }) => {
  const { forms } = useEntityTypes()
  const { data: form } = usePublishedForm(widget.title ? null : forms.find((f) => f.entityType === widget.entityType)?.code)
  const byKey = useMemo(() => Object.fromEntries((form ? allFields(form) : []).map((f) => [f.fieldKey, f])), [form])
  return children(widget.title || autoTitle(widget, byKey, entityLabelPlural(widget.entityType)))
}

const NewDashboardDialog = ({ open, onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const create = useMutation({
    mutationFn: () => dashboardsAPI.create({ name: name.trim(), isDefault: false, widgets: [] }),
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'dashboards'] })
      setName('')
      onClose()
      navigate(`/dashboards/${d.id}`)
    },
  })
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth TransitionProps={{ onExited: () => create.reset() }}
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); if (name.trim()) create.mutate() } }}>
      <DialogTitle>New dashboard</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {create.error && <Alert severity="error">{getErrorMessage(create.error)}</Alert>}
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus inputProps={{ maxLength: 100 }} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={create.isPending || !name.trim()}>Create</Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * No dashboard yet: admins can start from a suggested one built from the published forms
 * (a count per record type, then a breakdown and a trend for the first type with a dropdown).
 */
const NoDashboard = ({ isAdmin }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const notify = useNotify()
  const { entityTypes, forms, isLoading } = useEntityTypes()

  const starter = useMutation({
    mutationFn: async () => {
      const widgets = []
      for (const type of entityTypes.slice(0, 3)) {
        widgets.push({ ...newWidget(type), id: widgetId(), kind: 'NUMBER', title: `New ${entityLabelPlural(type).toLowerCase()}`, compare: true, width: 4 })
      }
      for (const type of entityTypes) {
        const form = await formsAPI.published(forms.find((f) => f.entityType === type).code)
        const fields = allFields(form)
        const group = fields.find((f) => f.fieldType === 'SELECT')
        if (!group) continue
        const money = fields.find((f) => f.fieldType === 'CURRENCY') || fields.find((f) => f.fieldType === 'NUMBER' || f.fieldType === 'DECIMAL')
        widgets.push({ ...newWidget(type), id: widgetId(), kind: 'BAR', groupBy: group.fieldKey, fn: money ? 'SUM' : 'COUNT', measureField: money?.fieldKey || null, dateField: 'none', width: 6 })
        widgets.push({ ...newWidget(type), id: widgetId(), kind: 'TREND', width: 6 })
        if (money) widgets.push({ ...newWidget(type), id: widgetId(), kind: 'TABLE', fn: 'SUM', measureField: money.fieldKey, dateField: 'none', limit: 5, width: 6 })
        break
      }
      return dashboardsAPI.create({ name: 'Overview', isDefault: true, widgets })
    },
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'dashboards'] })
      notify('Dashboard created. Edit it to adjust any widget.')
      navigate(`/dashboards/${d.id}`)
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const blank = useMutation({
    mutationFn: () => dashboardsAPI.create({ name: 'Overview', isDefault: true, widgets: [] }),
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'dashboards'] })
      navigate(`/dashboards/${d.id}`)
    },
  })

  if (isLoading) return <Loading />
  return (
    <>
      <Typography variant="h4" sx={{ mb: 3 }}>Home</Typography>
      {isAdmin && entityTypes.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <EmptyState icon={<InsightsOutlinedIcon />} title="Create your first dashboard"
            description="Track totals and trends across your records. Start from a suggested layout and adjust it, or start blank."
            action={(
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button variant="contained" startIcon={<AutoAwesomeOutlinedIcon />} onClick={() => starter.mutate()} disabled={starter.isPending}>
                  {starter.isPending ? 'Creating…' : 'Suggested dashboard'}
                </Button>
                <Button onClick={() => blank.mutate()} disabled={blank.isPending}>Start blank</Button>
              </Stack>
            )} />
        </Paper>
      )}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
        {entityTypes.map((type) => <EntityCountCard key={type} entityType={type} />)}
      </Box>
      {entityTypes.length === 0 && (
        <Paper variant="outlined">
          <EmptyState title="No record types yet"
            description={isAdmin ? 'Start from a ready-made template for your business (sales, real estate, lending, hiring, support), or build your own forms.' : 'An admin needs to publish a form first.'}
            action={isAdmin && (
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button variant="contained" onClick={() => navigate('/settings/templates')}>Browse templates</Button>
                <Button onClick={() => navigate('/forms/new')}>Create a form</Button>
              </Stack>
            )} />
        </Paper>
      )}
    </>
  )
}

const EntityCountCard = ({ entityType }) => {
  const navigate = useNavigate()
  const tenantId = useTenantKey()
  const { data, isLoading } = useQuery({ queryKey: [tenantId, 'records', entityType, 'count'], queryFn: () => recordsAPI.list({ entityType, size: 1 }) })
  return (
    <Card>
      <CardActionArea onClick={() => navigate(`/records/${entityType}`)}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">{entityLabelPlural(entityType)}</Typography>
          <Typography variant="h4" sx={{ mt: 1 }}>{isLoading ? <Skeleton width={48} /> : data?.totalItems ?? '—'}</Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default DashboardPage
